import { useState, useCallback } from 'react';
import cascadeDeleteService from '../services/cascadeDeleteService';

/**
 * Hook para gerenciar o fluxo de cascade delete em 2 fases.
 *
 * Estados: idle → loading-impact → preview → deleting → done/error
 *
 * Uso:
 *   const cascade = useCascadeDelete();
 *   // Abrir preview: cascade.requestDelete('/professionals/abc-123')
 *   // Confirmar: cascade.confirmDelete()
 *   // Resetar: cascade.reset()
 */
export default function useCascadeDelete() {
  const [state, setState] = useState('idle'); // idle | loading-impact | preview | deleting | done | error
  const [impact, setImpact] = useState(null);
  const [error, setError] = useState(null);
  const [deletePath, setDeletePath] = useState(null);

  const reset = useCallback(() => {
    setState('idle');
    setImpact(null);
    setError(null);
    setDeletePath(null);
  }, []);

  /**
   * Fase 1: Solicita preview de impacto.
   * @param {string} path - Caminho da API para DELETE
   */
  const requestDelete = useCallback(async (path) => {
    setState('loading-impact');
    setError(null);
    setDeletePath(path);

    try {
      const data = await cascadeDeleteService.getImpact(path);
      setImpact(data);
      setState('preview');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar impacto da exclusão');
      setState('error');
    }
  }, []);

  /**
   * Fase 2: Confirma e executa a exclusão.
   * Em caso de 409 (impacto mudou), atualiza o preview automaticamente.
   */
  const confirmDelete = useCallback(async () => {
    // Bloqueio de invariante do serviço dono (ex.: página publicada precisa ser despublicada
    // antes): nem chega a chamar a API. O backend também devolve confirmHash vazio, então este
    // guard é a segunda barreira, não a única.
    if (impact?.blocked) return;

    if (!impact?.confirmHash || !deletePath) return;

    setState('deleting');
    setError(null);

    try {
      await cascadeDeleteService.confirmDelete(deletePath, impact.confirmHash);
      setState('done');
    } catch (err) {
      if (err.response?.status === 409) {
        // ATENÇÃO: 409 aqui tem DOIS significados diferentes.
        // (a) blocked=true  → invariante do serviço dono; NÃO re-buscar preview, senão o
        //     usuário clica em confirmar e o diálogo "reseta" sozinho, parecendo bug.
        // (b) sem blocked   → o impacto mudou; comportamento original (atualiza o preview).
        if (err.response?.data?.blocked) {
          setImpact((prev) => ({ ...(prev || {}), ...err.response.data }));
          setState('preview');
        } else {
          setImpact(err.response.data);
          setError('O impacto da exclusão mudou. Revise os novos impactos antes de confirmar.');
          setState('preview');
        }
      } else {
        setError(err.response?.data?.message || err.message || 'Erro ao executar exclusão');
        setState('error');
      }
    }
  }, [impact, deletePath]);

  return {
    state,
    impact,
    error,
    isLoading: state === 'loading-impact',
    isDeleting: state === 'deleting',
    isDone: state === 'done',
    isPreview: state === 'preview',
    // Bloqueio vindo do backend (DeleteImpactResponse.blocked). Serialização camelCase.
    isBlocked: !!impact?.blocked,
    blockedReason: impact?.blockedReason || null,
    blockedMessage: impact?.blockedMessage || null,
    // Aviso que NÃO impede a exclusão — o irmão não-bloqueante do acima. Diz o que se perde
    // além das linhas listadas em impactGroups (ex.: a página publicada que para de agendar).
    warningReason: impact?.warningReason || null,
    warningMessage: impact?.warningMessage || null,
    requestDelete,
    confirmDelete,
    reset,
  };
}
